import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import './pdfScrollViewer.css';

import 'react-pdf/dist/Page/TextLayer.css';

type PdfScrollViewerProps = {
  /** Can be raw base64 OR a full data URL: "data:application/pdf;base64,...." */
  base64: string;

  /** Optional: set max width for each page (px). Default 900 */
  maxPageWidth?: number;

  /** Optional: render at a given scale instead of width-based sizing */
  scale?: number;

  /** Optional: start page (1-indexed), default 1 */
  startPage?: number;

  origin?: string;

  /** Optional: scroll to a specific page without reloading the document */
  scrollToPage?: number;
  scrollBehavior?: ScrollBehavior;
};

type ErrorDivProps = {
  message: string;
};

const ErrorDiv: React.FC<ErrorDivProps> = ({ message }) => (
  <div
    role="alert"
    style={{
      margin: '16px auto',
      maxWidth: 520,
      width: '100%',
      borderRadius: 10,
      border: '1px solid #f0b4b4',
      background: '#fff5f5',
      color: '#8b1f1f',
      padding: '12px 14px',
      textAlign: 'center',
      fontSize: 14,
      fontWeight: 500,
      boxShadow: '0 1px 4px rgba(139, 31, 31, 0.12)'
    }}
  >
    {message}
  </div>
);

export const PdfScrollViewer: React.FC<PdfScrollViewerProps> = ({
  base64,
  maxPageWidth = 900,
  scale,
  startPage = 1,
  origin,
  scrollToPage,
  scrollBehavior = 'smooth'
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(maxPageWidth);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const didInitialScrollRef = useRef<boolean>(false);
  const [targetRendered, setTargetRendered] = useState<boolean>(false);
  const [showAllPages, setShowAllPages] = useState<boolean>(false);

  // Resize observer so pages fit container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (!w) return;
      // cap width so you don't render huge pages on ultra-wide screens
      setContainerWidth(Math.min(w, maxPageWidth));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxPageWidth]);

  const onLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  };

  const pages = useMemo(() => {
    if (origin === 'qTable') {
      return Array.from({ length: numPages }, (_, i) => i + 1);
    }
    if (!numPages) return [];
    const start = Math.max(1, Math.min(startPage, numPages));
    if (!showAllPages) return [start];
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [numPages, showAllPages, startPage]);

  useEffect(() => {
    didInitialScrollRef.current = false;
    setTargetRendered(false);
  }, [base64, numPages, startPage]);

  useEffect(() => {
    setShowAllPages(false);
  }, [base64]);

  useEffect(() => {
    if (scrollToPage) {
      setShowAllPages(true);
    }
  }, [scrollToPage]);

  useEffect(() => {
    if (!scrollToPage || !numPages) return;
    const target = Math.max(1, Math.min(scrollToPage, numPages));
    const el = pageRefs.current[target];
    const container = containerRef.current;
    if (!el || !container) return;
    requestAnimationFrame(() => {
      container.scrollTo({
        top: el.offsetTop - 16,
        behavior: scrollBehavior
      });
    });
  }, [scrollToPage, numPages, pages, scrollBehavior]);

  useEffect(() => {
    if (!numPages || !targetRendered || didInitialScrollRef.current) return;
    const target = Math.max(1, Math.min(startPage, numPages));
    const el = pageRefs.current[target];
    const container = containerRef.current;
    if (!el || !container) return;
    const top = el.offsetTop - 16;
    requestAnimationFrame(() => {
      container.scrollTo({ top, behavior: 'auto' });
      didInitialScrollRef.current = true;
    });
  }, [numPages, startPage, targetRendered]);

  const containerStyle =
    origin === 'qTable'
      ? {
          height: '85vh',
          width: '100%',
          overflow: 'scroll',
          padding: 16,
          paddingTop: '5%'
          // boxSizing: 'border-box'
        }
      : {
          height: '75vh',
          width: '100%',
          overflow: 'scroll',
          padding: 16
          // boxSizing: 'border-box'
        };

  return (
    <div ref={containerRef} style={containerStyle}>
      <Document
        file={base64}
        onLoadSuccess={onLoadSuccess}
        loading={<div>Loading PDF…</div>}
        error={<ErrorDiv message="Failed to load PDF." />}
        noData={<ErrorDiv message="No PDF provided." />}
        // options={{
        //   // These can reduce “worker fetch” issues in some setups
        //   disableAutoFetch: false,
        //   disableStream: false
        // }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {pages.map((pageNumber) => (
            <div
              key={pageNumber}
              ref={(node) => {
                pageRefs.current[pageNumber] = node;
              }}
              style={{
                background: 'white',
                borderRadius: 8,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                padding: 12,
                width: 'fit-content',
                margin: '0 auto'
              }}
            >
              <Page
                pageNumber={pageNumber}
                width={scale ? undefined : containerWidth}
                scale={scale}
                renderAnnotationLayer
                renderTextLayer
                loading={<div>Loading page {pageNumber}…</div>}
                onRenderSuccess={() => {
                  const target = Math.max(1, Math.min(startPage, numPages));
                  if (pageNumber === target) {
                    setTargetRendered(true);
                  }
                }}
              />
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  opacity: 0.65,
                  textAlign: 'center'
                }}
              >
                Page {pageNumber} / {numPages}
              </div>
            </div>
          ))}
        </div>
      </Document>
      {!showAllPages && numPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
          <button
            type="button"
            onClick={() => setShowAllPages(true)}
            style={{
              border: '1px solid rgba(0,0,0,0.2)',
              borderRadius: 8,
              padding: '8px 14px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            Show entire doc
          </button>
        </div>
      )}
    </div>
  );
};
